import {
  type ComponentType,
  type Context,
  createContext,
  type PropsWithChildren,
  useContext,
} from "react";
import { TabsPrimitive } from "ui";

import { createNavigationTabsContent } from "./createNavigationTabsContent";
import { createNavigationTabsList } from "./createNavigationTabsList";

export interface NavigationTab {
  tabId: string;
  label: string;
  href: string;
  disabled?: boolean;
}

export interface NavigationTabsComponents {
  Link: ComponentType<PropsWithChildren<{ to: string }>>;
}

export interface NavigationTabsContextValue {
  components: NavigationTabsComponents;
  getCurrentTabId: () => string;
  tabs: NavigationTab[];
}

export interface NavigationPartProps extends PropsWithChildren {
  className?: string;
}

export interface NavigationTabsBundle {
  Context: Context<NavigationTabsContextValue>;
  useContext: () => NavigationTabsContextValue;
  TabsRoot: ComponentType<NavigationPartProps>;
  TabsList: ComponentType<NavigationPartProps>;
  TabsContent: ComponentType<NavigationPartProps>;
}

export function createNavigationTabsContext(): NavigationTabsBundle {
  const NavigationContext = createContext<NavigationTabsContextValue>(null!);

  function useNavigationContext() {
    return useContext(NavigationContext);
  }

  function TabsRoot(props: { className?: string } & PropsWithChildren) {
    const ctx = useNavigationContext();
    return (
      <TabsPrimitive.Root
        value={ctx.getCurrentTabId()}
        className={props.className}
      >
        {props.children}
      </TabsPrimitive.Root>
    );
  }

  const TabsList = createNavigationTabsList(useNavigationContext);
  const NavigationTabsContent =
    createNavigationTabsContent(useNavigationContext);

  return {
    Context: NavigationContext,
    useContext: useNavigationContext,
    TabsRoot: TabsRoot,
    TabsList: TabsList,
    TabsContent: NavigationTabsContent,
  };
}

export const PrimaryNavigationTabs = createNavigationTabsContext();
